import { z } from "zod";

/**
 * User-driven query builder over the inventory + procurement domain.
 *
 * This module defines the wire contract shared by the frontend (field picker,
 * criteria grid) and the backend (validation + SQL compilation):
 *
 *  - The **field catalog** (`SubjectAreaMeta` / `FieldMeta`) is the *public*
 *    description of what can be queried. It carries display metadata only —
 *    never physical table/column names. The backend registry maps each field
 *    key to a real column; that mapping never leaves the server.
 *  - The **query IR** (`QueryIRSchema`) is the JSON a user's query compiles to.
 *    It references fields by their catalog `key` only. The backend validates it
 *    against the registry and compiles it to parameterized SQL — every value is
 *    a bound parameter, every identifier comes from the registry, never the IR.
 *
 * See docs/projectplans/inventory-procurement-query-builder.md.
 */

// ─── Primitives ─────────────────────────────────────────────────────────────

/** The data shape of a field — drives which operators and aggregates are valid. */
export const QB_FIELD_DATA_TYPES = [
  "string",
  "number",
  "boolean",
  "date",
  "enum",
] as const;
export type QbFieldDataType = (typeof QB_FIELD_DATA_TYPES)[number];

/** Comparison operators available in the criteria grid. */
export const QB_OPERATORS = [
  "eq",
  "neq",
  "lt",
  "lte",
  "gt",
  "gte",
  "between",
  "in",
  "nin",
  "contains",
  "startsWith",
  "endsWith",
  "isNull",
  "isNotNull",
  // Relative-date operators (date fields only): match rows whose date falls
  // within the last / next N units relative to the current date. They carry a
  // `relativeDate` spec on the condition instead of a literal `value`.
  "withinLast",
  "withinNext",
] as const;
export type QbOperator = (typeof QB_OPERATORS)[number];

/** Operators that take no value (unary). */
export const QB_UNARY_OPERATORS: readonly QbOperator[] = [
  "isNull",
  "isNotNull",
];

/**
 * Binary operators valid when comparing one field directly against another
 * (`field <op> rightField`) or filtering an aggregate (HAVING). These are the
 * ordering/equality operators that make sense between two columns — no `like`,
 * `in`, or unary operators.
 */
export const QB_FIELD_COMPARISON_OPERATORS = [
  "eq",
  "neq",
  "lt",
  "lte",
  "gt",
  "gte",
] as const;
export type QbFieldComparisonOperator =
  (typeof QB_FIELD_COMPARISON_OPERATORS)[number];

/** Relative-date operators that consume a `relativeDate` spec (no literal value). */
export const QB_RELATIVE_DATE_OPERATORS: readonly QbOperator[] = [
  "withinLast",
  "withinNext",
];

/** Units a relative-date window or a date bucket can be expressed in. */
export const QB_RELATIVE_DATE_UNITS = ["day", "week", "month", "year"] as const;
export type QbRelativeDateUnit = (typeof QB_RELATIVE_DATE_UNITS)[number];

/** Date-bucket granularities for `date_trunc` group dimensions. */
export const QB_DATE_TRUNC_UNITS = [
  "day",
  "week",
  "month",
  "quarter",
  "year",
] as const;
export type QbDateTruncUnit = (typeof QB_DATE_TRUNC_UNITS)[number];

/** Aggregate functions for the summary (group-by) grid. */
export const QB_AGGREGATES = [
  "count",
  "countDistinct",
  "sum",
  "avg",
  "min",
  "max",
] as const;
export type QbAggregate = (typeof QB_AGGREGATES)[number];

export const QB_SORT_DIRECTIONS = ["asc", "desc"] as const;
export type QbSortDirection = (typeof QB_SORT_DIRECTIONS)[number];

/** Allowed leaf filter values: a scalar, or an array for `in`/`between`. */
const QbScalarSchema = z.union([z.string(), z.number(), z.boolean()]);
const QbFilterValueSchema = z.union([QbScalarSchema, z.array(QbScalarSchema)]);
export type QbFilterValue = z.infer<typeof QbFilterValueSchema>;

/**
 * A user-supplied output-column alias. The compiler interpolates aliases into
 * SQL as a quoted identifier (`AS "<alias>"`), so they must be constrained to
 * safe identifier characters here — at the contract boundary — to close any
 * identifier-injection vector before the value ever reaches SQL.
 */
export const QbSqlAliasSchema = z
  .string()
  .regex(
    /^[A-Za-z_][A-Za-z0-9_]*$/,
    "Alias must be a valid SQL identifier (letters, digits, underscores; not starting with a digit)",
  )
  .max(63, "Alias must be at most 63 characters");

// ─── Public field catalog (display metadata only) ────────────────────────────

/** Metadata describing one queryable field, surfaced to the field picker. */
export const FieldMetaSchema = z.object({
  /**
   * Stable key the IR references. Unique within its subject area; the registry
   * namespaces lookups by `${subjectArea}:${key}`, so the same key (e.g.
   * "item_sku") is intentionally reused across subjects.
   */
  key: z.string(),
  /** Human label shown in the picker and as the default column header. */
  label: z.string(),
  /** Subject area this field belongs to (e.g. "inventory_position"). */
  subjectArea: z.string(),
  /** Picker grouping label (GAINS-style category, e.g. "SKU level fields"). */
  category: z.string(),
  dataType: z.enum(QB_FIELD_DATA_TYPES),
  /** Operators valid for this field in the criteria grid. */
  operators: z.array(z.enum(QB_OPERATORS)),
  /** Whether the field can be used as an aggregate measure. */
  aggregatable: z.boolean(),
  /** Whether the field can be used as a group-by / sort dimension. */
  groupable: z.boolean(),
  /** For `enum` fields: the allowed values (drives multi-select dropdowns). */
  enumValues: z.array(z.string()).optional(),
  /** True if reaching this field requires joining beyond the base subject. */
  requiresJoin: z.boolean(),
  /** Shown in the curated "simple" faceted form, not just the advanced builder. */
  common: z.boolean(),
  /** Optional one-line help text. */
  description: z.string().optional(),
});
export type FieldMeta = z.infer<typeof FieldMetaSchema>;

/** A subject area = a base row grain the user can query from. */
export const SubjectAreaMetaSchema = z.object({
  key: z.string(),
  label: z.string(),
  /** One-line description of the row grain (e.g. "one row per SKU × warehouse"). */
  description: z.string(),
  /** Subject keys this base can join to (for cross-document queries). */
  joinableSubjects: z.array(z.string()),
});
export type SubjectAreaMeta = z.infer<typeof SubjectAreaMetaSchema>;

/**
 * A correlated relationship from one subject to another, surfaced so the UI can
 * offer "has matching <target>" filters (e.g. inventory_position → sales). The
 * sub-filter on a relationship condition is expressed in terms of the target
 * subject's fields.
 */
export const RelationshipMetaSchema = z.object({
  /** Relationship key, referenced by a `RelationCondition.relation`. */
  key: z.string(),
  /** Human label, e.g. "Sales". */
  label: z.string(),
  /** Subject this relationship is defined on. */
  subjectArea: z.string(),
  /** Subject the relationship points to (whose fields the sub-filter uses). */
  targetSubject: z.string(),
  /** Display label for the target subject. */
  targetLabel: z.string(),
  description: z.string().optional(),
});
export type RelationshipMeta = z.infer<typeof RelationshipMetaSchema>;

export const QueryCatalogSchema = z.object({
  subjectAreas: z.array(SubjectAreaMetaSchema),
  fields: z.array(FieldMetaSchema),
  relationships: z.array(RelationshipMetaSchema),
});
export type QueryCatalog = z.infer<typeof QueryCatalogSchema>;

// ─── Query IR ─────────────────────────────────────────────────────────────

/**
 * A relative-date window for the `withinLast` / `withinNext` operators, e.g.
 * `{ count: 90, unit: "day" }` → "within the last 90 days". Compiled to a bound
 * `CURRENT_DATE ± interval` predicate server-side; the LLM/UI never emits SQL.
 */
export const RelativeDateSpecSchema = z.object({
  count: z.number().int().positive(),
  unit: z.enum(QB_RELATIVE_DATE_UNITS),
});
export type RelativeDateSpec = z.infer<typeof RelativeDateSpecSchema>;

/**
 * A single criterion. Exactly one comparison source is used:
 *  - `value` — compare against a literal (the common case);
 *  - `rightField` — compare against another field in the same subject
 *    (`field <op> rightField`, e.g. `shipped_quantity < quantity`), restricted
 *    to {@link QB_FIELD_COMPARISON_OPERATORS} and type-compatible operands;
 *  - `relativeDate` — for `withinLast` / `withinNext`;
 *  - none — for the unary `isNull` / `isNotNull` operators.
 */
export const FilterConditionSchema = z
  .object({
    field: z.string(),
    operator: z.enum(QB_OPERATORS),
    /** Literal value. Omitted for unary, relative-date, or field-to-field ops. */
    value: QbFilterValueSchema.optional(),
    /** Compare against another field in the same subject (binary ops only). */
    rightField: z.string().optional(),
    /** Relative-date window for `withinLast` / `withinNext`. */
    relativeDate: RelativeDateSpecSchema.optional(),
  })
  /**
   * Normalize to exactly one comparison source, mirroring the compiler's
   * precedence. LLM tool-callers (and lenient clients) tend to over-populate the
   * optional fields — e.g. send `rightField: ""` and a `relativeDate` alongside
   * a `value` — because the mutual-exclusion can't be expressed in JSON Schema.
   * Rather than reject (which makes an LLM loop on retries), we strip the
   * irrelevant fields: relative-date operators keep `relativeDate`; otherwise a
   * non-empty `rightField` wins (field-to-field); otherwise the literal `value`.
   */
  .transform(
    (
      c,
    ): {
      field: string;
      operator: QbOperator;
      value?: QbFilterValue;
      rightField?: string;
      relativeDate?: RelativeDateSpec;
    } => {
      const isRelative =
        c.operator === "withinLast" || c.operator === "withinNext";
      const hasRightField =
        typeof c.rightField === "string" && c.rightField.trim() !== "";
      if (isRelative) {
        return {
          field: c.field,
          operator: c.operator,
          relativeDate: c.relativeDate,
        };
      }
      if (hasRightField) {
        return {
          field: c.field,
          operator: c.operator,
          rightField: c.rightField,
        };
      }
      return {
        field: c.field,
        operator: c.operator,
        ...(c.value !== undefined ? { value: c.value } : {}),
      };
    },
  );
export type FilterCondition = z.infer<typeof FilterConditionSchema>;

/** Whether a relationship condition requires matching rows to exist or not. */
export const QB_RELATION_QUANTIFIERS = ["exists", "notExists"] as const;
export type QbRelationQuantifier = (typeof QB_RELATION_QUANTIFIERS)[number];

/**
 * A threshold on the NUMBER of matching related rows, e.g. "more than 3 matching
 * sales". When present on a relationship condition it replaces the plain
 * exists/notExists test with a counted comparison (`COUNT(matching) <op> value`).
 */
export interface RelationCount {
  operator: QbFieldComparisonOperator;
  value: number;
}

/**
 * A correlated cross-document condition: "this row has (or has no) matching
 * rows in a related subject, optionally narrowed by a sub-filter on the target
 * subject's fields". Compiles to an EXISTS / NOT EXISTS subquery — or, when
 * `count` is set, to a `COUNT(matching) <op> value` comparison (e.g. more than 3
 * matching sales). Example: inventory positions whose product `has` a sales line
 * with order_date in range.
 */
export interface RelationCondition {
  /** Relationship key declared on the current subject (see RelationshipMeta). */
  relation: string;
  quantifier: QbRelationQuantifier;
  /** Optional sub-filter, expressed over the TARGET subject's fields. */
  filter?: FilterGroup;
  /**
   * Optional count threshold. When set, the condition tests the number of
   * matching rows (`COUNT(matching) <op> value`) instead of mere existence;
   * `quantifier` is ignored.
   */
  count?: RelationCount;
}

/**
 * A nestable AND/OR group of conditions. Recursive, so the criteria grid can
 * express `(a AND b) OR (c AND d)`, and conditions may be plain field criteria
 * or correlated relationship (cross-document) criteria.
 */
export interface FilterGroup {
  combinator: "and" | "or";
  conditions: Array<FilterCondition | RelationCondition | FilterGroup>;
}

export const RelationCountSchema = z.object({
  operator: z.enum(QB_FIELD_COMPARISON_OPERATORS),
  value: z.number().int().min(0),
});

export const RelationConditionSchema: z.ZodType<RelationCondition> = z.lazy(
  () =>
    z.object({
      relation: z.string(),
      quantifier: z.enum(QB_RELATION_QUANTIFIERS),
      filter: FilterGroupSchema.optional(),
      count: RelationCountSchema.optional(),
    }),
);

export const FilterGroupSchema: z.ZodType<FilterGroup> = z.lazy(() =>
  z.object({
    combinator: z.enum(["and", "or"]),
    conditions: z.array(
      z.union([
        FilterConditionSchema,
        RelationConditionSchema,
        FilterGroupSchema,
      ]),
    ),
  }),
);

/** A selected aggregate measure for the summary grid. */
export const AggregateSelectionSchema = z.object({
  field: z.string(),
  fn: z.enum(QB_AGGREGATES),
  /** Output column alias; defaults to `${fn}_${field}` server-side. */
  alias: QbSqlAliasSchema.optional(),
});
export type AggregateSelection = z.infer<typeof AggregateSelectionSchema>;

/**
 * A column projected from a related subject as a correlated aggregate — e.g.
 * "number of sales lines for this SKU" or "sum of sales qty in the last 90
 * days". Compiles to a correlated scalar subquery, so it adds a column WITHOUT
 * changing the base row grain (no fan-out). `field` is a field key on the
 * relationship's target subject (omitted only for `count`); `filter` optionally
 * narrows the related rows (over target-subject fields).
 */
export const RelationAggregateSchema = z.object({
  /** Relationship key on the current subject (see RelationshipMeta). */
  relation: z.string(),
  fn: z.enum(QB_AGGREGATES),
  /** Target-subject field key; required for every fn except `count`. */
  field: z.string().optional(),
  /** Optional sub-filter over the target subject's fields. */
  filter: FilterGroupSchema.optional(),
  /** Output column alias; defaults server-side. */
  alias: QbSqlAliasSchema.optional(),
});
export type RelationAggregate = z.infer<typeof RelationAggregateSchema>;

/**
 * A date-bucket group dimension: groups rows by `date_trunc(unit, field)`, e.g.
 * sales lines bucketed by month. Like `groupBy`, its presence switches the query
 * to summary mode; it adds a column (and a GROUP BY term) without needing a
 * pre-bucketed registry field. `field` must reference a `date` field.
 */
export const DateBucketSchema = z.object({
  field: z.string(),
  unit: z.enum(QB_DATE_TRUNC_UNITS),
  /** Output column alias; defaults to `${field}_${unit}` server-side. */
  alias: QbSqlAliasSchema.optional(),
});
export type DateBucket = z.infer<typeof DateBucketSchema>;

/**
 * A post-aggregation filter (HAVING): keeps only groups whose aggregate measure
 * satisfies the comparison, e.g. `HAVING sum(line_total) > 10000`. Only valid in
 * summary mode; `field` + `fn` re-derive the same aggregate expression the
 * compiler emits, so it never depends on a fragile alias.
 */
export const HavingConditionSchema = z.object({
  field: z.string(),
  fn: z.enum(QB_AGGREGATES),
  operator: z.enum(QB_FIELD_COMPARISON_OPERATORS),
  value: z.number(),
});
export type HavingCondition = z.infer<typeof HavingConditionSchema>;

export const SortSelectionSchema = z.object({
  /**
   * A field key, or an output-column alias (an aggregate, date bucket, or
   * related-column alias) so summary results can be ordered by their measures.
   */
  field: z.string(),
  direction: z.enum(QB_SORT_DIRECTIONS).default("asc"),
});
export type SortSelection = z.infer<typeof SortSelectionSchema>;

export const QB_MAX_ROW_LIMIT = 1000;
export const QB_DEFAULT_ROW_LIMIT = 100;

/**
 * The full query intermediate representation. Built by the UI, validated and
 * compiled server-side. References fields by catalog `key` only.
 */
export const QueryIRSchema = z
  .object({
    /** Base subject area = the row grain. */
    base: z.string(),
    /** Detail columns to return (field keys). Empty when only aggregating. */
    select: z.array(z.string()).default([]),
    /** Optional AND/OR filter tree. */
    filter: FilterGroupSchema.optional(),
    /** Group-by dimension field keys (presence switches to summary mode). */
    groupBy: z.array(z.string()).default([]),
    /** Date-bucket group dimensions (also switch to summary mode). */
    dateBuckets: z.array(DateBucketSchema).default([]),
    /** Aggregate measures (summary mode). */
    aggregates: z.array(AggregateSelectionSchema).default([]),
    /** Post-aggregation filters on aggregate measures (summary mode). */
    having: z.array(HavingConditionSchema).default([]),
    /**
     * Correlated aggregate columns drawn from related subjects (detail mode):
     * surfaces related-entity data (e.g. recent sales) without changing grain.
     */
    relationColumns: z.array(RelationAggregateSchema).default([]),
    sort: z.array(SortSelectionSchema).default([]),
    limit: z
      .number()
      .int()
      .min(1)
      .max(QB_MAX_ROW_LIMIT)
      .default(QB_DEFAULT_ROW_LIMIT),
    offset: z.number().int().min(0).default(0),
    /**
     * When true the backend skips the COUNT query and returns a synthetic
     * totalCount based only on the rows actually fetched. Used by the live
     * preview so each keypress doesn't pay for an extra full-table scan.
     */
    skipTotalCount: z.boolean().optional().default(false),
  })
  .refine(
    (ir) =>
      ir.select.length > 0 ||
      ir.groupBy.length > 0 ||
      ir.dateBuckets.length > 0 ||
      ir.aggregates.length > 0 ||
      ir.relationColumns.length > 0,
    {
      message:
        "A query must select at least one field, group-by, date bucket, aggregate, or related column",
    },
  )
  .refine(
    // In summary mode every plain selected column must also be grouped.
    (ir) =>
      ir.groupBy.length === 0 || ir.select.every((f) => ir.groupBy.includes(f)),
    {
      message:
        "When grouping, every selected detail field must appear in groupBy (use an aggregate otherwise)",
    },
  )
  .refine(
    // Correlated relation columns are a detail-mode projection; they can't be
    // combined with any group dimension (which would change what they mean).
    (ir) =>
      ir.relationColumns.length === 0 ||
      (ir.groupBy.length === 0 && ir.dateBuckets.length === 0),
    {
      message: "Related columns cannot be combined with grouping",
    },
  )
  .refine(
    // HAVING filters aggregates, so it only has meaning in summary mode.
    (ir) =>
      ir.having.length === 0 ||
      ir.groupBy.length > 0 ||
      ir.dateBuckets.length > 0 ||
      ir.aggregates.length > 0,
    {
      message: "HAVING requires a group-by, date bucket, or aggregate",
    },
  );
export type QueryIR = z.infer<typeof QueryIRSchema>;
export type QueryIRInput = z.input<typeof QueryIRSchema>;

// ─── Run result ─────────────────────────────────────────────────────────────

export type QueryRow = Record<string, string | number | boolean | null>;

export const QueryResultColumnSchema = z.object({
  key: z.string(),
  label: z.string(),
  dataType: z.enum(QB_FIELD_DATA_TYPES),
});
export type QueryResultColumn = z.infer<typeof QueryResultColumnSchema>;
